import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageCircle, Clock, Phone } from 'lucide-react';

const WHATSAPP_NUMBER = '34692248724';

const serviceKeys = [
  'implants',
  'braces',
  'invisalign',
  'aesthetic',
  'restoration',
  'cleaning',
  'emergency',
] as const;

const WhatsAppBooking = () => {
  const { t, i18n } = useTranslation();

  const greetings: Record<string, string> = {
    en: 'Hello, I would like to book an appointment for',
    es: 'Hola, me gustaría reservar una cita para',
    fr: 'Bonjour, je voudrais prendre rendez-vous pour',
    de: 'Hallo, ich möchte einen Termin vereinbaren für',
  };

  const generalMessages: Record<string, string> = {
    en: 'Hello, I would like to book an appointment at Clinic Dental Bahia.',
    es: 'Hola, me gustaría reservar una cita en Clinic Dental Bahia.',
    fr: 'Bonjour, je voudrais prendre rendez-vous à la Clinic Dental Bahia.',
    de: 'Hallo, ich möchte einen Termin bei Clinic Dental Bahia vereinbaren.',
  };

  const openWhatsApp = (serviceKey?: string) => {
    const lang = i18n.language;
    let message: string;

    if (serviceKey) {
      const serviceName = t(`services.${serviceKey}.title`);
      const greeting = greetings[lang] || greetings.en;
      message = `${greeting} ${serviceName}.`;
    } else {
      message = generalMessages[lang] || generalMessages.en;
    }

    window.open(
      `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      '_blank'
    );
  };

  return (
    <section id="appointment" className="section bg-primary-50">
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="section-title">{t('whatsapp.title')}</h2>
          <p className="max-w-3xl mx-auto text-lg text-gray-600">
            {t('whatsapp.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="bg-[#25D366] px-8 py-6 text-white">
              <div className="flex items-center gap-3">
                <MessageCircle size={28} />
                <div>
                  <h3 className="text-xl font-bold">{t('whatsapp.heading')}</h3>
                  <p className="text-white/90 text-sm mt-1">
                    {t('whatsapp.description')}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-8">
              <p className="text-gray-700 font-medium mb-4">
                {t('whatsapp.selectService')}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {serviceKeys.map((key) => (
                  <button
                    key={key}
                    onClick={() => openWhatsApp(key)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 hover:border-[#25D366] hover:bg-green-50 transition-all duration-200 text-left group"
                  >
                    <div className="w-10 h-10 rounded-full bg-green-50 group-hover:bg-[#25D366] flex items-center justify-center transition-colors duration-200">
                      <MessageCircle
                        size={18}
                        className="text-[#25D366] group-hover:text-white transition-colors duration-200"
                      />
                    </div>
                    <span className="text-gray-800 font-medium text-sm">
                      {t(`services.${key}.title`)}
                    </span>
                  </button>
                ))}
              </div>

              <div className="border-t border-gray-100 pt-6">
                <button
                  onClick={() => openWhatsApp()}
                  className="w-full flex items-center justify-center gap-3 bg-[#25D366] hover:bg-[#20BD5C] text-white px-6 py-4 rounded-xl transition-all duration-200 font-semibold text-lg shadow-md hover:shadow-lg"
                >
                  <MessageCircle size={24} />
                  {t('whatsapp.generalBooking')}
                </button>
              </div>

              <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Clock size={16} />
                  <span>{t('whatsapp.responseTime')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone size={16} />
                  <span>+34 692 248 724</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatsAppBooking;
